import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Minus, Plus, Printer } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Brand } from "../components/Brand";
import { PassportSheet, PAPERS } from "../components/passport/PassportSheet";
import { PassportCard } from "../components/passport/PassportCard";
import { QrStickerCard } from "../components/passport/QrStickerCard";
import { API, PRINT_HEIGHTS, DEFAULT_HEIGHT } from "../lib/api";

const MAX_LABELS = 12;
const PRESETS = [1, 2, 4, 6, 12];

// Batch print of one duck's passport on a roll of Rollo 4x6" labels — one label per copy, high-contrast for thermal.
export default function LabelsPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [duck, setDuck] = useState(null);
  const [shipped, setShipped] = useState(null);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(Math.min(MAX_LABELS, Math.max(1, Number(params.get("qty")) || 4)));
  const [mode, setMode] = useState(params.get("mode") === "qr" ? "qr" : "passport");
  const [ink, setInk] = useState(params.get("ink") === "colour" ? "colour" : "bw");
  const height = PRINT_HEIGHTS.includes(Number(params.get("height"))) ? Number(params.get("height")) : DEFAULT_HEIGHT;

  useEffect(() => {
    axios
      .get(`${API}/share/${id}`)
      .then((r) => setDuck(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This duck could not be loaded."));
    axios
      .get(`${API}/share/${id}/order-status`)
      .then((r) => setShipped(["shipped", "delivered"].includes(r.data?.status) ? r.data : null))
      .catch(() => setShipped(null));
  }, [id]);

  const step = (d) => setQty((q) => Math.min(MAX_LABELS, Math.max(1, q + d)));

  return (
    <div className="App min-h-screen bg-dots print:bg-none print:min-h-0">
      <style>{`@page { size: ${PAPERS.rollo.css}; margin: 0; }`}</style>
      <header className="h-20 border-b border-border/70 bg-background/80 backdrop-blur-xl print:hidden">
        <div className="max-w-5xl mx-auto h-full px-4 sm:px-8 flex items-center justify-between gap-3">
          <Link to="/" data-testid="labels-logo-link" className="flex items-center gap-3">
            <Brand />
          </Link>
          <Link to={`/passport/${id}?height=${height}`} data-testid="labels-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Single passport
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10 print:p-0 print:max-w-none">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-5 print:hidden">
          <div>
            <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Rollo label batch</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2">
              <span data-testid="labels-qty-value">{qty}</span> labels, one roll
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              {mode === "qr"
                ? `Gift-box stickers: one big scannable code per 4×6″ label, ${qty} of them. Load the roll, print at 100% (no “fit to page”).`
                : `Same duck, ${qty} times — each copy prints on its own 4×6″ label. Load the roll, print at 100% (no “fit to page”).`}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="inline-flex rounded-full bg-muted/60 p-1">
                {[
                  ["passport", "Full passport"],
                  ["qr", "QR sticker"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`labels-mode-${key}`}
                    onClick={() => setMode(key)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${mode === key ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-full bg-muted/60 p-1">
                {[
                  ["bw", "Thermal B&W"],
                  ["colour", "Colour"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`labels-ink-${key}`}
                    onClick={() => setInk(key)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${ink === key ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="flex items-center rounded-full bg-muted/60 p-1">
              <button type="button" data-testid="labels-qty-dec" onClick={() => step(-1)} disabled={qty <= 1} className="grid place-items-center h-9 w-9 rounded-full hover:bg-background disabled:opacity-40">
                <Minus className="h-4 w-4" />
              </button>
              {PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`labels-qty-${n}`}
                  onClick={() => setQty(n)}
                  className={`rounded-full px-3 py-2 text-sm font-semibold transition-colors ${qty === n ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {n}
                </button>
              ))}
              <button type="button" data-testid="labels-qty-inc" onClick={() => step(1)} disabled={qty >= MAX_LABELS} className="grid place-items-center h-9 w-9 rounded-full hover:bg-background disabled:opacity-40">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button data-testid="labels-print-btn" onClick={() => window.print()} disabled={!duck} className="rounded-full h-11 gap-2 font-bold duck-shadow">
              <Printer className="h-4 w-4" /> Print {qty}
            </Button>
          </div>
        </div>

        {error && <div data-testid="labels-error" className="rounded-3xl border border-destructive/40 bg-card p-8 text-center text-destructive">{error}</div>}
        {!duck && !error && (
          <div className="grid place-items-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        )}
        {duck && (
          <div data-testid="labels-stack" className="space-y-8 print:space-y-0">
            {Array.from({ length: qty }, (_, i) => (
              <PassportSheet key={i} paper="rollo">
                {mode === "qr" ? (
                  <QrStickerCard duck={duck} height={height} colour={ink === "colour"} />
                ) : (
                  <PassportCard duck={duck} height={height} shipped={shipped} mono={ink === "bw"} />
                )}
              </PassportSheet>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
